import SessionConstants from '../config/SessionConstants.js';
import APIUtils from './APIUtils.js';


const Oauth = {

    //  Fallback used only when the token endpoint omits expires_in, so the client-side
    //  timeout is still armed. Observed SAMS expires_in is 3600s; this is deliberately
    //  shorter so an unknown lifetime errs toward warning too early rather than too late.
    ASSUMED_TOKEN_LIFETIME_MS: 30 * 60 * 1000,

    //  Dev login bypasses SAMS entirely (it exists so developers can keep working through a
    //  SAMS outage), so there is no expires_in to honour. Give it a fixed session length and
    //  drive the same warning + countdown off it.
    DEV_SESSION_LIFETIME_MS: 60 * 60 * 1000,

    //  Absolute maximum lifetime of a session regardless of activity. Recorded once at login
    //  (beginSession) and never extended, so continual navigation cannot keep a session alive
    //  forever. Because it is an absolute wall-clock deadline it is also what forces a logout
    //  when the recorded time passes while the machine is powered off. Tune per policy.
    SESSION_ABSOLUTE_LIFETIME_MS: 12 * 60 * 60 * 1000,

    //  How close to expiry a token must be before a navigation will spend a refresh on it.
    //  Above this margin isLoginValid trusts the recorded expiry and makes no auth call at
    //  all; below it (and before the timeout warning fires) an active navigator is renewed.
    //  Refreshing only inside this window keeps the single-use refresh token from rotating
    //  more than about once per token lifetime.
    REFRESH_MARGIN_MS: 10 * 60 * 1000,

    //  Holds the in-flight refresh so concurrent callers share one request. SAMS refresh
    //  tokens are single-use, so parallel refreshes would invalidate each other.
    _refreshPromise: null,

    //  Main function for checking if the current login is valid.
    //  This runs on every page component, so it is also where an active user's SAMS token is
    //  kept alive. Rather than refreshing on every navigation (which would rotate the
    //  single-use refresh token constantly), it trusts the recorded expiry and only spends a
    //  refresh once the token is within REFRESH_MARGIN_MS of expiring. An active navigator is
    //  therefore renewed about once per token lifetime, and never hits the hard expiry
    //  mid-work. Must return true for a page to load.
    async isLoginValid() {
        console.log('--Oauth.isLoginValid()');

        //  Enforce the absolute session cap before any renewal path runs. SESSION_HARD_EXPIRY
        //  is an absolute wall-clock deadline recorded once at login and never extended, so a
        //  session that outlived it - including one Chrome restored into a brand-new browser
        //  session after the machine was powered off - is ended here instead of being silently
        //  renewed below. There is no grace period: the user is forced to log in again.
        const hasAuthenticatedSession = this.samsTokenExists()
            || sessionStorage.getItem(SessionConstants.DEVLOGIN) === 'true';
        if (hasAuthenticatedSession && this.isSessionHardExpired()) {
            console.log('Session reached its absolute lifetime cap. Forcing re-login.');
            this.endExpiredSession();
            return false;
        }

        //check for sams token
        if (this.samsTokenExists()) {
            const remainingMs = Number(sessionStorage.getItem(SessionConstants.SAMS_TOKEN_EXPIRES_AT)) - Date.now();

            //  Plenty of life left: trust the recorded expiry and load the page with no auth
            //  network call at all. A token revoked server-side early is still caught by the
            //  401 interceptor, so trusting the local clock here cannot strand the user.
            if (remainingMs > this.REFRESH_MARGIN_MS) return true;

            //  The token has already fully expired: the idle timeout has elapsed, so the session
            //  must NOT be silently revived by a refresh (even if SAMS would still honour the
            //  refresh token). Force a re-login instead.
            if (remainingMs <= 0) {
                console.log('SAMS token has expired. Forcing re-login.');
                this.endExpiredSession();
                return false;
            }

            //  Still alive but near expiry: this is an actively-navigating user, so spend one
            //  refresh to extend the session.
            console.log('SAMS token near expiry. Attempting to refresh SAMS token');
            const refreshed = await this.refreshSamsToken();
            if (refreshed.ok) return true;
            if (refreshed.reason === 'transient') {
                //  SAMS was unreachable. Any life still left on the current token is enough to
                //  keep working; the 401 interceptor guards a genuine expiry.
                return remainingMs > 0;
            }

            //  Refresh was rejected outright: the session cannot be renewed, so end it and force
            //  a re-login. There is no grace period - a rejected refresh means the session is
            //  genuinely over.
            console.log('SAMS session could not be renewed. Forcing re-login.');
            this.endExpiredSession();
            return false;
        }
        //check for dev token
        if (sessionStorage.getItem(SessionConstants.DEVLOGIN) === 'true') {
            //  Enforce the idle deadline BEFORE renewing. The recorded expiry is an absolute
            //  wall-clock time, so a dev session whose window elapsed must be ended here rather
            //  than resurrected by the startDevSession() call below. There is no grace period -
            //  the developer is forced to log in again.
            const devExpiresAt = Number(sessionStorage.getItem(SessionConstants.SAMS_TOKEN_EXPIRES_AT));
            if (devExpiresAt && Date.now() >= devExpiresAt) {
                console.log('Local dev session has expired. Forcing re-login.');
                this.endExpiredSession();
                return false;
            }
            if (APIUtils.verifyLogin()) {
                //  Still inside the window: treat this navigation as activity and extend the
                //  idle timer only. The absolute cap set at login is never extended here, so an
                //  actively-navigating developer is still forced to re-authenticate eventually.
                //  startDevSession only refuses if a deadline already elapsed - both are checked
                //  above, so it succeeds here; honoring its result just guarantees we never
                //  return true on a session it declined to renew.
                if (this.startDevSession()) {
                    console.log('Local dev login is valid');
                    return true;
                }
                console.log('Local dev session could not be renewed. Forcing re-login.');
                this.endExpiredSession();
                return false;
            } 
            console.log('Local dev login is not valid. Forcing logout.');
            return false;
             
        } 
        //no token detected
        console.log('No valid SAMS token detected');
        return false;
    
    },

    //  samsTokenExists()
    //  Returns true if a SAMS token exists in the session
    samsTokenExists() {
        console.log('--Check if Oauth.samsTokenExists()');
        let samsToken = sessionStorage.getItem(SessionConstants.SAMS_ACCESS_TOKEN);
        return ((samsToken) && (samsToken != 'undefined')) 
    },

    //  setTokenExpiry(expiresInSeconds)
    //  Records when the current access token dies so the client-side session timeout can be
    //  armed from the real SAMS lifetime instead of only reacting to a 401.
    setTokenExpiry(expiresInSeconds) {
        const lifetime = Number(expiresInSeconds) > 0
            ? Number(expiresInSeconds) * 1000
            : this.ASSUMED_TOKEN_LIFETIME_MS;
        sessionStorage.setItem(SessionConstants.SAMS_TOKEN_EXPIRES_AT, String(Date.now() + lifetime));
    },

    //  startDevSession()
    //  Dev login has no SAMS token, so its session length is purely a client-side clock.
    //  Records a fresh fixed-length expiry; called at dev login and whenever a developer
    //  clicks "Continue Session". Returns true if the session was (re)started, false if it was
    //  refused because a deadline had already elapsed - so callers can force a logout instead
    //  of silently reviving a dead session.
    startDevSession() {
        //  Never resurrect a session whose deadline has already passed (e.g. the machine slept
        //  past the idle window or the absolute cap and the developer clicked Continue on
        //  resume before the countdown fired). This mirrors the elapsed-time checks in
        //  isLoginValid so "Continue" cannot extend a session that is really over. At a fresh
        //  login neither deadline is set/elapsed, so the session starts normally.
        if (this.isSessionHardExpired()) {
            return false;
        }
        const existingExpiry = Number(sessionStorage.getItem(SessionConstants.SAMS_TOKEN_EXPIRES_AT));
        if (existingExpiry && Date.now() >= existingExpiry) {
            return false;
        }
        sessionStorage.setItem(
            SessionConstants.SAMS_TOKEN_EXPIRES_AT,
            String(Date.now() + this.DEV_SESSION_LIFETIME_MS)
        );
        return true;
    },

    //  beginSession()
    //  Records the one-shot absolute session cap. Called only at a fresh login (SAMS or dev),
    //  never on the per-navigation renewal, so the cap is a true maximum session length that
    //  the idle-timer renewals in isLoginValid cannot push out.
    beginSession() {
        sessionStorage.setItem(
            SessionConstants.SESSION_HARD_EXPIRY,
            String(Date.now() + this.SESSION_ABSOLUTE_LIFETIME_MS)
        );
    },

    //  isSessionHardExpired()
    //  True once the absolute session cap has passed. Enforced on every navigation so a
    //  restored tab (Chrome rehydrates sessionStorage) or a session left open while the
    //  machine was powered off is ended instead of silently renewed. A missing cap (older
    //  sessions from before this was introduced) is treated as not-expired so they are not
    //  logged out unexpectedly; the next login records one.
    isSessionHardExpired() {
        const hardExpiry = Number(sessionStorage.getItem(SessionConstants.SESSION_HARD_EXPIRY));
        return Boolean(hardExpiry) && Date.now() >= hardExpiry;
    },

    //  setLoginAsValid()
    //  Sets session variables as required to indicate a valid login
    setLoginAsValid() {
        sessionStorage.setItem(SessionConstants.LOGOUT_STATUS, false);
        sessionStorage.setItem(SessionConstants.TIMEOUT_STATUS, false);
    },

    //  setLoginAsExpired()
    //  Sets session variables as required to indicate an expired login
    setLoginAsExpired() {
        sessionStorage.setItem(SessionConstants.LOGOUT_STATUS, true);
        sessionStorage.setItem(SessionConstants.TIMEOUT_STATUS, true);
        sessionStorage.setItem(SessionConstants.ERROR_DESCRIPTION, 'Your login for ED3N has expired. Please log in again to continue using ED3N.');
    },

    //  endExpiredSession()
    //  Ends a session that has timed out or can no longer be renewed: clears the auth/session
    //  variables, records the expired/timeout status so the landing page can explain why the
    //  user was signed out, and terminates the SAMS SSO session. Clearing only the local
    //  session would leave the SAMS SSO cookie alive, so the next login could silently re-auth
    //  the user straight back in (a "half-dead" logout); routing through performLogout closes
    //  that gap so every expiry path forces a genuine re-authentication. There is no grace
    //  period - the user must log in again.
    endExpiredSession() {
        //  Capture whether this was a dev session before clearAllSessionVariables removes the
        //  DEVLOGIN flag, so performLogout can skip the (pointless) SAMS logout for dev logins.
        const wasDevLogin = sessionStorage.getItem(SessionConstants.DEVLOGIN) === 'true';
        this.clearAllSessionVariables();
        this.setLoginAsExpired();
        this.performLogout(wasDevLogin);
    },

    //  getSamsToken(code)
    //  Sets the SAMS tokens using an access code received from SAMS
    async getSamsTokens(code) {
        console.log('--Oauth.getSamsTokens()');
        try {
            var tokenData = await APIUtils.getSamsTokens(code);
            var accessToken = tokenData.accessToken;
            var refreshToken = tokenData.refreshToken;
            var expiresIn = tokenData.expiresIn;
            if ((accessToken) && (accessToken != 'undefined')) {
                console.log('Successfully authenticated with SAMS');
                sessionStorage.setItem(SessionConstants.SAMS_ACCESS_TOKEN, accessToken);
                //  Guard the write: setItem coerces undefined to the string "undefined",
                //  which would look like a real token and break every later refresh.
                if ((refreshToken) && (refreshToken != 'undefined')) {
                    sessionStorage.setItem(SessionConstants.SAMS_REFRESH_TOKEN, refreshToken);
                } else {
                    console.error('[Oauth] SAMS returned no refresh token; this session will not be refreshable.');
                }
                this.setTokenExpiry(expiresIn);
                //  Record the one-shot absolute session cap at the moment of a fresh login. The
                //  per-navigation refresh in isLoginValid extends only the token expiry, never
                //  this, so even a long-lived SAMS refresh token cannot keep the session alive
                //  past the maximum lifetime.
                this.beginSession();
            } else {
                console.log('Unable to obtain a SAMS access token');
                sessionStorage.setItem(SessionConstants.ERROR_MESSAGE, 'Could not login to ED3N');
                sessionStorage.setItem(SessionConstants.ERROR_DESCRIPTION, 'Unable to authenticate with SAMS');
                this.clearAllSessionVariables();
            }
        } catch (error) {
            console.log('***ERRORS RETURNED***');
            console.error('Error: ', error);
            sessionStorage.setItem(SessionConstants.ERROR_MESSAGE, 'Could not login to ED3N');
            sessionStorage.setItem(SessionConstants.ERROR_DESCRIPTION, 'Unable to authenticate with SAMS');
        }
    },

    //  getSamsUserInfo(token)
    //  Get the SAMS user info for the given SAMS access token
    async getUserInfoUsingSams() {
        console.log('--Oauth.getUserInfoUsingSams()');
        try {
            var samsUserInfo = await APIUtils.getSamsUserInfo();
            if (samsUserInfo) {
                var userId = samsUserInfo.email.toUpperCase();
                var ed3nUserInfo = await APIUtils.getEd3nUserInfo(userId);
                if (ed3nUserInfo) {
                    this.setAllSessionVariables(ed3nUserInfo);
                } else {
                    console.error('Unable to retrieve user information from ED3N database.')
                    sessionStorage.setItem(SessionConstants.ERROR_MESSAGE, 'Could not login to ED3N');
                    sessionStorage.setItem(SessionConstants.ERROR_DESCRIPTION, 'Unable to retrieve user information from ED3N database');
                    this.clearAllSessionVariables();
                }
            } else {
                console.error('Unable to retrieve user information from SAMS.')
                sessionStorage.setItem(SessionConstants.ERROR_MESSAGE, 'Could not login to ED3N');
                sessionStorage.setItem(SessionConstants.ERROR_DESCRIPTION, 'Unable to retrieve user information from SAMS');
                this.clearAllSessionVariables();
            }
        } catch (error) {
            console.log('***ERRORS RETURNED***')
            console.error('Error: ', error)
            sessionStorage.setItem(SessionConstants.ERROR_MESSAGE, 'Could not login to ED3N');
            sessionStorage.setItem(SessionConstants.ERROR_DESCRIPTION, 'Unable to authenticate with SAMS');
        }
    },

    async getUserInfoUsingDev() {
        console.log('--Oauth.getUserInfoUsingDev()');
        try {
            var userInfo = await APIUtils.getEd3nUserInfo(sessionStorage.getItem(SessionConstants.DEVLOGIN_USERNAME));
            if (userInfo) {
                var userId = userInfo.email;
                this.setAllSessionVariables(userInfo);
                this.beginSession();
                this.startDevSession();
            } else {
                console.error('Unable to retrieve user information from Dev login.')
                sessionStorage.setItem(SessionConstants.ERROR_MESSAGE, 'Could not login to ED3N');
                sessionStorage.setItem(SessionConstants.ERROR_DESCRIPTION, 'Unable to retrieve user information from ED3N');
                this.clearAllSessionVariables();
            }
        } catch (error) {
            console.log('***ERRORS RETURNED***')
            console.error('Error: ', error)
            sessionStorage.setItem(SessionConstants.ERROR_MESSAGE, 'Could not login to ED3N');
            sessionStorage.setItem(SessionConstants.ERROR_DESCRIPTION, 'Unable to retrieve user information from ED3N');
        }
    },

    //  refreshSamsToken()
    //  Refreshes the SAMS tokens. Resolves to { ok, reason } where reason is:
    //      'rejected'  - SAMS refused the grant. The session is genuinely dead.
    //      'transient' - the call could not be completed (network, 5xx, partial response).
    //                    This says nothing about session validity and must NOT be
    //                    presented to the user as an expired session.
    //  Single-flighted: concurrent callers (401 interceptor, proactive check, timeout
    //  modal) share one request, because a SAMS refresh token can only be spent once.
    async refreshSamsToken() {
        if (this._refreshPromise) return this._refreshPromise;
        this._refreshPromise = this._performRefresh()
            .finally(() => { this._refreshPromise = null; });
        return this._refreshPromise;
    },

    async _performRefresh() {
        console.log('--Oauth.refreshSamsToken()');

        const token = sessionStorage.getItem(SessionConstants.SAMS_REFRESH_TOKEN);
        if (!token || token === 'undefined') {
            console.error('[Oauth] No SAMS refresh token available; the session cannot be refreshed.');
            return { ok: false, reason: 'rejected' };
        }

        let tokens;
        try {
            tokens = await APIUtils.refreshSamsTokens(token);
        } catch (error) {
            const status = error && error.response ? error.response.status : null;
            //  Only the token endpoint saying "no" proves the session is over. Everything
            //  else is an infrastructure failure and should be logged as an error.
            if (status === 400 || status === 401) {
                console.error('[Oauth] SAMS rejected the refresh token.', status, error.response.data);
                return { ok: false, reason: 'rejected' };
            }
            console.error('[Oauth] SAMS refresh request failed.', status || (error && error.message));
            return { ok: false, reason: 'transient' };
        }

        //  The refresh token we just sent is now spent, so the response must carry a
        //  complete replacement pair. Writing a partial response would store the string
        //  "undefined" over a working token and permanently break the session, so treat
        //  an incomplete response as a failure and leave the existing tokens untouched.
        const accessToken = tokens ? tokens.accessToken : null;
        const refreshToken = tokens ? tokens.refreshToken : null;
        const isUsable = (value) => Boolean(value) && value !== 'undefined';

        if (!isUsable(accessToken) || !isUsable(refreshToken)) {
            console.error('[Oauth] SAMS refresh response did not include a complete token pair; existing tokens kept.');
            return { ok: false, reason: 'transient' };
        }

        //  A logout or a newer login may have replaced or cleared the refresh token while this
        //  request was in flight. Committing now would resurrect a session the user just ended,
        //  so only write back if the stored refresh token is still the one we spent.
        if (sessionStorage.getItem(SessionConstants.SAMS_REFRESH_TOKEN) !== token) {
            console.error('[Oauth] Discarding a refresh response for a session that is no longer current.');
            return { ok: false, reason: 'transient' };
        }

        console.log('Successfully refreshed SAMS tokens.');
        sessionStorage.setItem(SessionConstants.SAMS_ACCESS_TOKEN, accessToken);
        sessionStorage.setItem(SessionConstants.SAMS_REFRESH_TOKEN, refreshToken);
        this.setTokenExpiry(tokens.expiresIn);
        return { ok: true, reason: null };
    },

    //  clearAllTokens()
    //  clear all token session variables
    clearAllSessionVariables() {
        sessionStorage.removeItem(SessionConstants.SAMS_ACCESS_TOKEN);
        sessionStorage.removeItem(SessionConstants.SAMS_REFRESH_TOKEN);
        sessionStorage.removeItem(SessionConstants.SAMS_TOKEN_EXPIRES_AT);
        sessionStorage.removeItem(SessionConstants.PROGRAM_ID);
        sessionStorage.removeItem(SessionConstants.USER_EMAIL);
        sessionStorage.removeItem(SessionConstants.USER_FIRSTNAME);
        sessionStorage.removeItem(SessionConstants.USER_LASTNAME);
        sessionStorage.removeItem(SessionConstants.NBS_PROGRAM);
        sessionStorage.removeItem(SessionConstants.IS_MANAGER);
        sessionStorage.removeItem(SessionConstants.IS_SYSADMIN);
        sessionStorage.removeItem(SessionConstants.IS_IMPERSONATING);
        sessionStorage.removeItem(SessionConstants.CURRENT_USER);
        sessionStorage.removeItem(SessionConstants.DEVLOGIN);
        //  Dev login stores the developer's credentials for the request interceptor's Basic
        //  auth header; clear them on logout so a plaintext password does not linger in
        //  sessionStorage after the session ends.
        sessionStorage.removeItem(SessionConstants.DEVLOGIN_USERNAME);
        sessionStorage.removeItem(SessionConstants.DEVLOGIN_PASSWORD);
        sessionStorage.removeItem(SessionConstants.SESSION_ENDED);
        sessionStorage.removeItem(SessionConstants.SESSION_HARD_EXPIRY);
        sessionStorage.removeItem(SessionConstants.HARDCAP_WARN_30_FOR);
        sessionStorage.removeItem(SessionConstants.HARDCAP_WARN_5_FOR);
        //  Clear any impersonation remnants so a signed-out sysadmin leaves no original-user
        //  identity behind. sessionStorage can be rehydrated by a Chrome tab-restore, so these
        //  must be wiped here rather than relying on the tab closing.
        sessionStorage.removeItem(SessionConstants.ORIGINAL_USER_EMAIL);
        sessionStorage.removeItem(SessionConstants.ORIGINAL_USER_FIRSTNAME);
        sessionStorage.removeItem(SessionConstants.ORIGINAL_USER_LASTNAME);
        sessionStorage.removeItem(SessionConstants.ORIGINAL_CURRENT_USER);
        sessionStorage.removeItem(SessionConstants.ORIGINAL_PROGRAM_ID);
        sessionStorage.removeItem(SessionConstants.ORIGINAL_PROGRAM_NAME);
        sessionStorage.removeItem(SessionConstants.ORIGINAL_IS_MANAGER);
        sessionStorage.removeItem(SessionConstants.ORIGINAL_IS_SYSADMIN);
        //  One-shot "already shown this session" flag; clear it so the next user who logs in on
        //  a restored tab still sees the biochemical alert.
        sessionStorage.removeItem(SessionConstants.BIOCHEMICAL_ALERT_SHOWN);
    },

    //  performLogout(isDevLogin)
    //  Performs logout and redirects appropriately.
    //  For a dev login, skips SAMS entirely (it never authenticated through SAMS) and returns
    //  to the landing page - checked first so a dev login on localhost does not fire a
    //  pointless SAMS logout request.
    //  For a SAMS login on localhost, redirects to landing immediately and fires SAMS logout
    //  in the background.
    //  For a SAMS login on dev/prod, uses the standard SAMS logout with target redirect.
    //  isDevLogin must be passed by callers that have already cleared session state, because
    //  the DEVLOGIN flag is gone by then; it defaults to reading the flag for any other caller.
    performLogout(isDevLogin = sessionStorage.getItem(SessionConstants.DEVLOGIN) === 'true') {
        const isLocalhost = typeof __IS_LOCALHOST__ !== 'undefined' && __IS_LOCALHOST__;
        const Paths = require('../config/Paths.js').default;
        
        if (isDevLogin) {
            //  Dev login (available on local/dev/test) never authenticated through SAMS - it
            //  exists so developers can work through a SAMS outage - so there is no SAMS SSO
            //  session to end. Routing it to the SAMS logout URL would be pointless (and on a
            //  deployed env could hang if SAMS is down), so just return to the landing page.
            window.location.assign('/landing');
        } else if (isLocalhost) {
            // Fire and forget: attempt to logout from SAMS in background using a GET request
            // This happens asynchronously and won't block the redirect
            fetch(Paths.samsLogout, {
                method: 'GET',
                mode: 'no-cors',
                credentials: 'include',
                keepalive: true
            }).catch(() => {});
            
            // Immediately redirect to landing page (synchronous)
            window.location.assign('/landing');
        } else {
            // Development/Production SAMS session: end the SSO session via SAMS logout with redirect
            window.location.assign(Paths.samsLogout);
        }
    },

    //  setAllSessionVariables(userInfo)
    //  Sets all user session variables from user info object
    setAllSessionVariables(userInfo) {
        sessionStorage.setItem(SessionConstants.PROGRAM_ID, userInfo.programId);
        sessionStorage.setItem(SessionConstants.USER_EMAIL, userInfo.email);
        sessionStorage.setItem(SessionConstants.USER_FIRSTNAME, userInfo.firstName);
        sessionStorage.setItem(SessionConstants.USER_LASTNAME, userInfo.lastName);
        sessionStorage.setItem(SessionConstants.NBS_PROGRAM, userInfo.programId);
        sessionStorage.setItem(SessionConstants.IS_MANAGER, (userInfo.roleName == 'PROGRAMADMIN' || userInfo.roleName == 'SYSADMIN'));
        // If CURRENT_USER is not set, treat this as the first (real) login and
        // initialize SYSADMIN and CURRENT_USER. When impersonating we set
        // CURRENT_USER beforehand, so this block will be skipped during impersonation.
        if (!sessionStorage.getItem(SessionConstants.CURRENT_USER)) {
            sessionStorage.setItem(SessionConstants.IS_SYSADMIN, userInfo.roleName == 'SYSADMIN');
            sessionStorage.setItem(SessionConstants.CURRENT_USER, userInfo.email);
        }
    }
};

export default Oauth;
